import React, { useState, useCallback, useMemo } from 'react';
// FT-357: shell-level back handler — register a "back to dashboard" handler
// so mobile users always see a Back button in the bottom bar.
import { useSetBackHandler } from '../../../contexts/NavigationBackContext';
import { devLog } from '../../../utils/devLog';
import TabNavigation from '../TabNavigation';
import PlannedMaintenanceTab from './maintenance/PlannedMaintenanceTab';
import EquipmentRecordsTab from './maintenance/EquipmentRecordsTab';
import MaintenanceHistoryView from './maintenance/MaintenanceHistoryView';
import MaintenanceCheckView from './maintenance/MaintenanceCheckView';
import MaintenanceCheckViewMobile from './maintenance/MaintenanceCheckViewMobile';
import AddCustomJobModal from './maintenance/AddCustomJobModal';  // FT-186: Edit modal
import CloseLinkedDefectPrompt from './maintenance/CloseLinkedDefectPrompt';  // FT-353e
import { useStorageState } from '../../../hooks/useStorageState';
import { useVessel } from '../../../contexts/VesselContext';
import { useCrew } from '../../../hooks/useCrew';
import { useCustomDialogs } from '../../../hooks/useCustomDialogs';
import { useMaintenanceData } from '../../../hooks/domain/useMaintenanceData';
import { generateSequentialId, ID_PREFIXES } from '../../../utils/sequentialIdGenerator';
import { useRequirementsBadge } from '../../../hooks/useRequirementsBadge';
import { updateEquipmentAfterMaintenance } from '../../../utils/maintenanceEquipmentLinker';
import { serviceTypeFromJob } from '../../../services/maintenance/serviceType';
import { getOrgId } from '../../../utils/getOrgId';
import { getJobDisplayName } from '../../../utils/maintenanceJobHelpers';
import { useMaintenanceJobsDomain } from '../../../hooks/domain/maintenance/useMaintenanceJobsDomain';  // FT-182 v12: For archiveJob
import { useDefects } from '../../../hooks/domain/defects/useDefects';  // FT-353e: post-completion close-linked-defect prompt
import { useAuth } from '../auth/AuthGate';  // FT-346: captured-by attribution for <PhotoAttachment>
import { usePermissions } from '../../../hooks/usePermissions';
// FT-349c: draft stale-sweep on module mount (once per session).
import { sweepStaleDrafts } from '../../../hooks/domain/maintenance/useMaintenanceDraft';
import { usePhotoStore } from '../../common/PhotoAttachment/hooks/usePhotoStore';

const MaintenanceModule = ({ setActiveModule, setActiveModuleWithTab, setShowVesselSettingsView }) => {
  // FT-357: register a shell-level "back to dashboard" handler so mobile
  // users always see a Back button in the bottom bar for this module.
  useSetBackHandler(
    useCallback(() => {
      if (setActiveModule) setActiveModule('dashboard');
    }, [setActiveModule])
  );

  // Check for tab preference from navigation
  const initialTab = sessionStorage.getItem('moduleTab_maintenance') || 'planned';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showCheckView, setShowCheckView] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState('all'); // Persist category filter
  const [editJobData, setEditJobData] = useState(null);  // FT-186: Job being edited

  // Clean up tab preference after reading
  React.useEffect(() => {
    if (initialTab !== 'planned') {
      sessionStorage.removeItem('moduleTab_maintenance');
    }
  }, []);

  // Listen for tab switch events (keeping for potential future use)
  React.useEffect(() => {
    const handleTabSwitch = (event) => {
      const { tab } = event.detail || {};
      if (tab) {

        setActiveTab(tab);
      }
    };

    window.addEventListener('maintenance:switchTab', handleTabSwitch);
    return () => window.removeEventListener('maintenance:switchTab', handleTabSwitch);
  }, []);

  // Add sync event logging for debugging (dev only)
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      const handleSyncEvent = (e) => {
        const detail = e.detail || {};
        devLog('[SYNC]', {
          module: detail.module || detail.baseKey || 'unknown',
          key: detail.key,
          vesselId: detail.vesselId,
          timestamp: detail.timestamp
        });
      };
      window.addEventListener('sr:storage:set', handleSyncEvent);
      return () => window.removeEventListener('sr:storage:set', handleSyncEvent);
    }
  }, []);

  // Check for equipment detail navigation from defects module
  React.useEffect(() => {
    const equipmentToShow = sessionStorage.getItem('equipment:showDetail');
    if (equipmentToShow) {

      // Switch to equipment tab
      setActiveTab('equipment');

      // Clear the sessionStorage flag (consumed)
      sessionStorage.removeItem('equipment:showDetail');
    }
  }, []);

  // Check for specific tab navigation (e.g., from other modules)
  React.useEffect(() => {
    const targetTab = sessionStorage.getItem('moduleTab_maintenance');
    if (targetTab) {

      setActiveTab(targetTab);

      // Clear the sessionStorage flag (consumed)
      sessionStorage.removeItem('moduleTab_maintenance');
    }
  }, []);

  // Check if mobile
  const [isMobile, setIsMobile] = useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const { currentVesselId, getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();

  // Get vessel config for requirements badge - memoized to prevent infinite loops
  const vesselConfig = React.useMemo(() => currentVessel ? {
    vesselName: currentVessel.name,
    vesselType: currentVessel.type,
    engineType: currentVessel.engineType || 'diesel',
    category: currentVessel.category,
    tonnage: currentVessel.tonnage,
    operatingArea: currentVessel.operatingArea,
    carriesPassengers: currentVessel.carriesPassengers
  } : null, [currentVessel?.name, currentVessel?.type, currentVessel?.engineType, currentVessel?.category, currentVessel?.tonnage, currentVessel?.operatingArea, currentVessel?.carriesPassengers]);

  // Requirements badge integration
  const { hasUpdates, checking, runSync } = useRequirementsBadge('inspections', vesselConfig);

  // Use the NEW useCrew hook - single source of truth!
  const { crew, loading: crewLoading } = useCrew();

  // Use domain hook for maintenance data - no direct storage access!
  const {
    history: maintenanceHistory,
    equipment: equipmentFromHook,
    getByEquipmentType,
    getLastCompleted,
    completeMaintenanceJob,
    signOffCompletion
  } = useMaintenanceData();

  // Keep defects and equipment_register separate for now (should have their own domain hooks)
  const { value: defects, setValue: saveDefects } = useStorageState('defects', []);
  const { value: equipment = equipmentFromHook, setValue: saveEquipment } = useStorageState('equipment_register', equipmentFromHook);
  // Use getLastCompleted from domain hook for schedule calculations
  const maintenanceLastCompleted = getLastCompleted();
  const { showAlert, AlertDialog } = useCustomDialogs();


  // FT-182 v12: Get archiveJob from domain hook for Delete button in CheckView
  // FT-183: add/update for edit modal save path
  const { archive: archiveJob, add: domainAddJob, update: domainUpdateJob } = useMaintenanceJobsDomain();

  // FT-353e: post-completion "Close linked defect?" prompt. Modal is triggered
  // only when the completion links back to a defect that is still Open or In
  // Progress. Per advisor decision #2 — NO auto-close, prompt only. ISM sign-
  // off is a human decision.
  // FT-353f: same hook provides handleCorrectiveJobDeleted for the delete cascade.
  const { findById: findDefectById, updateDefect, handleCorrectiveJobDeleted } = useDefects();
  const [closeDefectPrompt, setCloseDefectPrompt] = useState(null);  // { defect, completedJobId }

  // FT-346: captured-by attribution string for <PhotoAttachment> — same
  // pattern as FT-345 EquipmentDetailView.
  const { user: currentUser } = useAuth();
  const capturedBy = currentUser?.name || currentUser?.email || 'unknown';

  const { can } = usePermissions();
  const canSignMaintenance = useMemo(() => can('sign', 'maintenance'), [can]);

  // FT-349a: `handleUpdateJobPhotos` (FT-346) removed — photos moved from the
  // JOB entity to the COMPLETION entity. FT-349b will manage `photos` inside
  // the check-view's local state and pass them through in the onSave payload.

  // FT-349c: boot-time stale draft sweep. Runs once per session per module
  // mount — cascade-deletes photos for drafts older than 30 days and drops
  // the localStorage key. Guarded by sessionStorage so tab reloads within
  // the same session don't re-sweep.
  const { deleteByRecord } = usePhotoStore(currentVesselId);
  React.useEffect(() => {
    if (!currentVesselId) return;
    const SWEEP_KEY = 'maintenance_draft_swept_v1';
    if (sessionStorage.getItem(SWEEP_KEY)) return;
    (async () => {
      try {
        const result = await sweepStaleDrafts(deleteByRecord);
        sessionStorage.setItem(SWEEP_KEY, String(Date.now()));
        devLog('[MaintenanceModule] FT-349c stale sweep', result);
      } catch (err) {
        devLog('[MaintenanceModule] FT-349c stale sweep failed', err);
      }
    })();
  }, [currentVesselId, deleteByRecord]);

  // Step 1: Performance monitoring - establish baseline before reactive fix
  React.useEffect(() => {
    const startTime = performance.now();

    return () => {
      const endTime = performance.now();

    };
  });

  devLog('[MaintenanceModule] Using new useCrew hook - crew:', crew);
  devLog('[MaintenanceModule] Crew count:', crew?.length || 0);
  if (crew && crew.length > 0) {
    devLog('[MaintenanceModule] First crew member details:', {
      id: crew[0].id,
      name: crew[0].name,
      full_name: crew[0].full_name,
      position: crew[0].position,
      allKeys: Object.keys(crew[0])
    });
  }

  const tabs = [
    { id: 'planned', label: 'Planned Maintenance' },  // Most frequent - first
    { id: 'equipment', label: 'Equipment Register' },
    { id: 'history', label: 'Maintenance History' }
  ];

  const handleOpenCheck = (job) => {
    setSelectedJob(job);
    setShowCheckView(true);
  };

  const handleBackToSchedule = useCallback(() => {
    setShowCheckView(false);
    setSelectedJob(null);
    // Force refresh of the maintenance tab by changing the key
    setRefreshKey(prev => prev + 1);
  }, []);

  // Handle sync requirements
  const handleSyncRequirements = async () => {
    try {
      await runSync();
      // Force refresh of the maintenance tab
      setRefreshKey(prev => prev + 1);
    } catch (error) {

    }
  };

  // Note: Defect creation is now handled within completeMaintenanceJob
  // FT-182 v10: handleDeleteCustomJob removed - archiving now handled by PlannedMaintenanceTab via useMaintenanceJobsDomain
  // to ensure proper linking with maintenance records

  // FT-353d: navigate to the Defects module so the defect chip on a
  // maintenance job card can jump back to the originating defect. Mirrors
  // DefectsModule.handleNavigateToJob (FT-209 handler).
  const handleNavigateToDefect = (defectId) => {
    devLog('[MaintenanceModule] FT-353d: Navigating to defect:', defectId);
    if (setActiveModuleWithTab) {
      setActiveModuleWithTab('defects', 'active', { highlightDefectId: defectId });
    } else if (setActiveModule) {
      setActiveModule('defects');
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'planned':
        return <PlannedMaintenanceTab
          key={refreshKey}
          onOpenCheck={handleOpenCheck}
          maintenanceHistory={maintenanceHistory} // FIX: Pass full history array for the modal
          currentVesselId={currentVesselId}
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
          onSyncRequirements={handleSyncRequirements}
          hasUpdates={hasUpdates}
          checking={checking}
          onNavigateToDefect={handleNavigateToDefect}
        />;
      case 'equipment':
        return <EquipmentRecordsTab
          setActiveModule={setActiveModule}
          setActiveModuleWithTab={setActiveModuleWithTab}
          setShowVesselSettingsView={setShowVesselSettingsView}
        />;
      case 'history':
        return <MaintenanceHistoryView />;
      default:
        return <PlannedMaintenanceTab
          key={refreshKey}
          onOpenCheck={handleOpenCheck}
          maintenanceHistory={maintenanceHistory} // FIX: Pass full history array for the modal
          currentVesselId={currentVesselId}
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
          onSyncRequirements={handleSyncRequirements}
          hasUpdates={hasUpdates}
          checking={checking}
          onNavigateToDefect={handleNavigateToDefect}
        />;
    }
  };

  // Show full-screen check view if a job is selected
  if (showCheckView && selectedJob) {
    // Use mobile version on small screens
    const CheckViewComponent = isMobile ? MaintenanceCheckViewMobile : MaintenanceCheckView;

    return (
      <>
        <CheckViewComponent
          job={selectedJob}
          equipment={equipment || equipmentFromHook}
          maintenanceHistory={maintenanceHistory}  // Pass maintenance history
          crewList={crew}  // Pass crew list
          // FT-346: <PhotoAttachment> vessel + attribution wiring.
          // FT-349a: `onUpdateJobPhotos` removed — photos are now managed
          // inside the check view's local state and included in the onSave
          // payload against the completion (see completion.photos).
          currentVesselId={currentVesselId}
          capturedBy={capturedBy}
          onAddEquipment={(newEquipment) => {
            // Add new equipment to storage
            const updatedEquipment = [...equipment, { ...newEquipment, id: generateSequentialId(equipment, ID_PREFIXES.EQUIPMENT) }];
            saveEquipment(updatedEquipment);
          }}
          canSignOff={canSignMaintenance}
          onSignOff={signOffCompletion}
          onBack={handleBackToSchedule}
          onEdit={(job) => setEditJobData(job)}  // FT-186: Open edit modal
          onDelete={(jobId) => {
            // FT-182 v12: Archive job and return to schedule
            // FT-353f: cascade — clear the linked defect's correctiveJobId
            // back-reference (and revert status if safe) BEFORE the job
            // is archived. Ordering matters because handleCorrectiveJobDeleted
            // reads the defects array to find the back-reference; archiving
            // the job doesn't remove that pointer on its own.
            handleCorrectiveJobDeleted(jobId, capturedBy);
            archiveJob(jobId);
            handleBackToSchedule();
          }}
          onSave={(data) => {
            devLog('[MaintenanceModule] =====================================');
            devLog('[MaintenanceModule] MAINTENANCE SAVE STARTED');
            devLog('[MaintenanceModule] onSave called with data:', data);
            devLog('[MaintenanceModule] hasDefects:', data.hasDefects);
            devLog('[MaintenanceModule] defectsFound:', data.defectsFound);
            devLog('[MaintenanceModule] defectData:', data.defectData);

            // Unify "also create defect" to a single boolean
            const alsoCreateDefect = !!(data.hasDefects || data.defectsFound || data.defectData);
            devLog('[MaintenanceModule] Will create defect?', alsoCreateDefect);

            // Capture result with both historyId and defectId
            const result = completeMaintenanceJob({
              fields: {
                // FT-140 FIX: Capture job title/name properly for history records
                // Previously used equipmentType which resulted in "unknown" names
                // FT-186: Use canonical getJobDisplayName helper
                title: getJobDisplayName(selectedJob),
                notes: data.notes || '',
                jobId: data.jobId,
                equipmentType: selectedJob.equipmentType,
                checkType: selectedJob.checkType,
                date: data.date || new Date().toISOString().split('T')[0],
                inspector: data.inspector,
                inspectorName: data.inspectorName,  // FT-221: Pass crew name for history display
                allUnitsChecked: data.allUnitsChecked,
                hasDefects: data.defectsFound || data.hasDefects,  // Support both field names
                defectsFound: data.defectsFound || data.hasDefects,  // Include both for compatibility
                defectDetails: data.defectDetails,
                defectData: data.defectData || null,  // Pass full defect data if available
                defectSeverity: data.defectSeverity,
                defectPriority: data.defectPriority,
                defectNotes: data.defectNotes,
                equipmentChecks: data.equipmentChecks,
                // FT-349a: pass photos + completionId from the form payload.
                // Fallbacks (`?? undefined`) mean FT-349b can arrive
                // incrementally without breaking existing FT-346 tests that
                // don't yet include these keys.
                photos: data.photos ?? undefined,
                completionId: data.completionId ?? undefined
                // NO vesselId - storage layer handles scoping!
              },
              alsoCreateDefect,
              performedBy: data.inspector,
              equipmentIds: data.selectedEquipmentIds,  // NEW: Pass selected equipment IDs
              job: selectedJob  // Pass job object for interval/unit info
            });

            devLog('[MaintenanceModule] Result:', result);

            if (data.signOffNow && result?.historyId) {
              signOffCompletion(result.historyId);
            }

            // Update equipment records if units were selected
            if (data.selectedEquipmentUnits && data.selectedEquipmentUnits.length > 0) {
              devLog('[MaintenanceModule] Updating equipment units:', data.selectedEquipmentUnits);
              devLog('[MaintenanceModule] Job trackingMode:', selectedJob.trackingMode);

              // Get normalized service type for this job
              const serviceType = serviceTypeFromJob(selectedJob);
              devLog('[MaintenanceModule] Normalized serviceType:', serviceType);

              const updatedEquipment = updateEquipmentAfterMaintenance({
                selectedUnits: data.selectedEquipmentUnits,
                equipmentList: equipment,
                serviceRecord: {
                  date: data.date || new Date().toISOString().split('T')[0],
                  type: getJobDisplayName(selectedJob),
                  serviceType: serviceType, // CRITICAL: Add normalized service type
                  jobId: selectedJob.id,
                  inspector: data.inspector
                },
                intervalMonths: selectedJob.interval || 12
              });

              // Save updated equipment
              saveEquipment(updatedEquipment);
              devLog('[MaintenanceModule] Equipment updated - saved', updatedEquipment.length, 'records');

              // Emit event so job cards can refresh badges
              window.dispatchEvent(new CustomEvent('sr:equipment:updated', {
                detail: { vesselId: currentVesselId }
              }));
              devLog('[MaintenanceModule] Emitted sr:equipment:updated event');
            }

            // Show success message based on actual result
            const unitsUpdated = data.selectedEquipmentUnits?.length || 0;
            let message = 'Maintenance check completed successfully';
            if (result?.defectId && unitsUpdated > 0) {
              message = `Maintenance completed, ${unitsUpdated} units updated, and defect created`;
            } else if (result?.defectId) {
              message = 'Maintenance check completed and defect created';
            } else if (unitsUpdated > 0) {
              message = `Maintenance completed and ${unitsUpdated} units updated`;
            }
            showAlert(message, 'success');

            // FT-353e: post-completion — if this completion linked back to a
            // defect (either the just-created defect from the maintenance
            // form, or the inherited originatingDefectId from a corrective
            // job), and that defect is still Open or In Progress, prompt the
            // user to close it. NEVER auto-close (advisor decision #2).
            //
            // Skip conditions:
            //   • No linkedDefectId returned (routine job, no linkage)
            //   • Defect not found (stale ref — shouldn't happen but safe)
            //   • Defect already Closed / Resolved (nothing to do)
            //   • Defect was just created this same completion — the user
            //     explicitly reported it now, prompting to close it is nonsense
            const promptDefectId = result?.linkedDefectId && !result?.defectId
              ? result.linkedDefectId
              : null;
            if (promptDefectId) {
              const defect = findDefectById(promptDefectId);
              const status = (defect?.status || '').toString().toLowerCase();
              const alreadyClosed = status === 'closed' || status === 'resolved';
              if (defect && !alreadyClosed) {
                setCloseDefectPrompt({ defect, completedJobId: selectedJob?.id || data.jobId });
              }
            }

            // Always go back to schedule
            handleBackToSchedule();

            // Only navigate to Defects module if defect was actually created
            if (result?.defectId) {
              devLog('[MaintenanceModule] Defect created:', result.defectId, '- navigating to Defects module');
              setActiveModule('defects');
            }
          }}
        />
        {/* FT-186: Edit Job Modal */}
        {editJobData && (
          <AddCustomJobModal
            isOpen={!!editJobData}
            onClose={() => {
              setEditJobData(null);
            }}
            onSave={(updatedJob) => {
              domainUpdateJob(updatedJob.id, { ...updatedJob, custom: true });
              // Update selectedJob to reflect changes immediately
              setSelectedJob(prev => ({ ...prev, ...updatedJob }));
              setEditJobData(null);
              showAlert('Job updated successfully', 'success');
            }}
            initialData={editJobData}
          />
        )}
        <AlertDialog />
      </>
    );
  }

  // FT-353e: shared "Close linked defect?" modal handlers. Rendered from the
  // schedule fallback branch (we always call handleBackToSchedule() before
  // the prompt appears, so the check-view branch has already unmounted).
  const handleCloseLinkedDefect = (defect, completedJobId) => {
    if (!defect) return;
    const actor = currentUser?.name || currentUser?.email || 'Unknown';
    const historyEntry = {
      date: new Date().toISOString(),
      action: 'Auto-closed via completed corrective job',
      by: actor,
      notes: `Corrective job ${completedJobId || 'unknown'} completed and user confirmed closure`
    };
    updateDefect(defect.id, {
      status: 'closed',
      closedAt: Date.now(),
      history: [...(defect.history || []), historyEntry]
    });
    showAlert('Defect closed', 'success');
    setCloseDefectPrompt(null);
  };

  const handleKeepLinkedDefectOpen = () => {
    setCloseDefectPrompt(null);
  };

  return (
    <>
      <div>
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Equipment</h1>
          <p className="mt-1 text-sm text-gray-500">Manage vessel equipment, maintenance, and defects</p>
        </div>

        <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

        <div className="mt-6">
          {renderTabContent()}
        </div>
      </div>

      {/* FT-353e: post-completion prompt to close the linked defect. NO auto-
          close (advisor decision #2). User picks [Close defect] or [Not yet]. */}
      {closeDefectPrompt && (
        <CloseLinkedDefectPrompt
          defect={closeDefectPrompt.defect}
          completedJobId={closeDefectPrompt.completedJobId}
          onCloseDefect={handleCloseLinkedDefect}
          onKeepOpen={handleKeepLinkedDefectOpen}
        />
      )}

      <AlertDialog />
    </>
  );
};

export default MaintenanceModule;